import {
  onBeforeMount,
  onBeforeUnmount,
  provide,
  inject,
  ref,
  type InjectionKey,
  type Ref
} from 'vue'
import { emitter } from '@/plugins/emitter'
import {
  addHistoryListItem,
  moveHistoryListItem,
  type HistoryColumns
} from '@/utils/format-edit/historyList'
import type { NewRirekishoFormatData } from '@/composables/format-edit/useNewRirekishoFormatData'

const DEACTIVATE_HISTORY_ITEM = 'deactivate-history-item'

type AutoHistoryList = Record<
  string,
  { year: number; month: number; desc: string }
>

export type NewRirekishoHistories = {
  activeHistoryItem: Ref<string>
  setEducationalBackgrounds: (historyList: AutoHistoryList) => void
  setJobHistories: (historyList: AutoHistoryList) => void
  resetEducationalBackgrounds: () => void
  resetJobHistories: () => void
  addEducationalHistory: (
    index?: number | null,
    columns?: HistoryColumns
  ) => void
  deleteEducationalHistory: (index: number) => void
  moveEducationalHistory: (params: { index: number; to: string }) => void
  addJobHistory: (index?: number | null, columns?: HistoryColumns) => void
  deleteJobHistory: (index: number) => void
  moveJobHistory: (params: { index: number; to: string }) => void
  addLicenseHistory: (index?: number | null, columns?: HistoryColumns) => void
  deleteLicenseHistory: (index: number) => void
  moveLicenseHistory: (params: { index: number; to: string }) => void
  activateHistoryItem: (id: string) => void
  deactivateHistoryItem: () => void
}

const NEW_RIREKISHO_HISTORIES_KEY: InjectionKey<NewRirekishoHistories> = Symbol(
  'newRirekishoHistories'
)

export function provideNewRirekishoHistories(histories: NewRirekishoHistories) {
  provide(NEW_RIREKISHO_HISTORIES_KEY, histories)
}

export function useNewRirekishoHistoriesApi(): NewRirekishoHistories {
  const histories = inject(NEW_RIREKISHO_HISTORIES_KEY)
  if (!histories) {
    throw new Error(
      'useNewRirekishoHistoriesApi() must be used under NewRirekishoEdit'
    )
  }
  return histories
}

export function useNewRirekishoHistories(
  formatData: NewRirekishoFormatData
): NewRirekishoHistories {
  const activeHistoryItem = ref('')

  function addEducationalHistory(
    index?: number | null,
    columns?: HistoryColumns
  ) {
    if (formatData.educationalHistories == null) {
      formatData.educationalHistories = []
    }
    addHistoryListItem(formatData.educationalHistories, index, columns)
  }

  function addJobHistory(index?: number | null, columns?: HistoryColumns) {
    if (formatData.jobHistories == null) {
      formatData.jobHistories = []
    }
    addHistoryListItem(formatData.jobHistories, index, columns)
  }

  function addLicenseHistory(index?: number | null, columns?: HistoryColumns) {
    if (formatData.licenseHistories == null) {
      formatData.licenseHistories = []
    }
    addHistoryListItem(formatData.licenseHistories, index, columns)
  }

  function setEducationalBackgrounds(historyList: AutoHistoryList) {
    formatData.educationalHistories = []
    let index = 0
    for (const key in historyList) {
      const item = historyList[key]
      addEducationalHistory(index++, {
        year: item.year,
        month: item.month,
        description: item.desc
      })
    }
  }

  function setJobHistories(historyList: AutoHistoryList) {
    for (const key in historyList) {
      const item = historyList[key]
      addJobHistory(null, {
        year: item.year,
        month: item.month,
        description: item.desc
      })
    }
  }

  function resetEducationalBackgrounds() {
    formatData.educationalHistories = []
  }

  function resetJobHistories() {
    formatData.jobHistories = []
  }

  function deleteEducationalHistory(index: number) {
    formatData.educationalHistories.splice(index, 1)
  }

  function deleteJobHistory(index: number) {
    formatData.jobHistories.splice(index, 1)
  }

  function deleteLicenseHistory(index: number) {
    formatData.licenseHistories.splice(index, 1)
  }

  function moveEducationalHistory(params: { index: number; to: string }) {
    moveHistoryListItem(formatData.educationalHistories, params)
  }

  function moveJobHistory(params: { index: number; to: string }) {
    moveHistoryListItem(formatData.jobHistories, params)
  }

  function moveLicenseHistory(params: { index: number; to: string }) {
    moveHistoryListItem(formatData.licenseHistories, params)
  }

  function activateHistoryItem(id: string) {
    activeHistoryItem.value = id
    const element = document.getElementById(id)
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' })
    }
  }

  function deactivateHistoryItem() {
    activeHistoryItem.value = ''
  }

  onBeforeMount(() => {
    emitter.on(DEACTIVATE_HISTORY_ITEM, deactivateHistoryItem)
  })

  onBeforeUnmount(() => {
    emitter.off(DEACTIVATE_HISTORY_ITEM, deactivateHistoryItem)
  })

  return {
    activeHistoryItem,
    setEducationalBackgrounds,
    setJobHistories,
    resetEducationalBackgrounds,
    resetJobHistories,
    addEducationalHistory,
    deleteEducationalHistory,
    moveEducationalHistory,
    addJobHistory,
    deleteJobHistory,
    moveJobHistory,
    addLicenseHistory,
    deleteLicenseHistory,
    moveLicenseHistory,
    activateHistoryItem,
    deactivateHistoryItem
  }
}
